import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Fuel,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Ship,
  Timer,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import operationsApi from '@/services/operationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTimezone } from '@/contexts/TimezoneContext';

const DIRECTION_OPTIONS = ['NORTE', 'NORDESTE', 'LESTE', 'SUDESTE', 'SUL', 'SUDOESTE', 'OESTE', 'NOROESTE'];

const toLocalInputValue = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const numberFormat = (value, decimals = 1) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const defaultStop = () => toLocalInputValue(new Date());
const defaultStart = () => toLocalInputValue(new Date(Date.now() - 24 * 60 * 60 * 1000));
const newRegimeId = () => `regime_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;

const toEditableNumber = (value) => (value === null || value === undefined ? '' : String(value));
const toPayloadNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const findCondition = (conditions, metric, operator) =>
  (conditions || []).find((condition) => condition.metric === metric && condition.operator === operator);

const fieldFromConditions = (regime, key, metric, operator, field = 'value') => {
  if (regime[key] !== undefined && regime[key] !== null) return regime[key];
  const condition = findCondition(regime.conditions, metric, operator);
  return condition ? condition[field] : null;
};

const normalizeRegimeForEditor = (regime = {}) => {
  const conditions = regime.conditions || [];
  const rpmBetween = findCondition(conditions, 'avg_rpm', 'between');
  const speedBetween = findCondition(conditions, 'speed_kmh', 'between');
  const fuelBetween = findCondition(conditions, 'fuel_rate_lh', 'between');
  const directionCondition = findCondition(conditions, 'direction', 'in');
  const geofenceConditions = conditions.filter(
    (condition) => condition.metric === 'active_geofence' && condition.operator === 'contains'
  );

  return {
    id: regime.id || newRegimeId(),
    name: regime.name || 'Novo regime',
    color: regime.color || '#0ea5e9',
    priority: toEditableNumber(regime.priority ?? 10),
    min_duration_minutes: toEditableNumber(regime.min_duration_minutes ?? 5),
    rpm_min: toEditableNumber(regime.rpm_min ?? rpmBetween?.min ?? fieldFromConditions(regime, 'rpm_min', 'avg_rpm', '>=')),
    rpm_max: toEditableNumber(regime.rpm_max ?? rpmBetween?.max ?? fieldFromConditions(regime, 'rpm_max', 'avg_rpm', '<=')),
    speed_min_kmh: toEditableNumber(regime.speed_min_kmh ?? speedBetween?.min ?? fieldFromConditions(regime, 'speed_min_kmh', 'speed_kmh', '>=')),
    speed_max_kmh: toEditableNumber(regime.speed_max_kmh ?? speedBetween?.max ?? fieldFromConditions(regime, 'speed_max_kmh', 'speed_kmh', '<=')),
    fuel_rate_min_lh: toEditableNumber(regime.fuel_rate_min_lh ?? fuelBetween?.min ?? fieldFromConditions(regime, 'fuel_rate_min_lh', 'fuel_rate_lh', '>=')),
    fuel_rate_max_lh: toEditableNumber(regime.fuel_rate_max_lh ?? fuelBetween?.max ?? fieldFromConditions(regime, 'fuel_rate_max_lh', 'fuel_rate_lh', '<=')),
    directions: regime.directions || directionCondition?.values || [],
    geofence_names: regime.geofence_names || geofenceConditions.map((condition) => condition.value).filter(Boolean),
  };
};

const addNumericConditions = (conditions, metric, min, max) => {
  if (min !== null && max !== null) {
    conditions.push({ metric, operator: 'between', min, max });
    return;
  }
  if (min !== null) conditions.push({ metric, operator: '>=', value: min });
  if (max !== null) conditions.push({ metric, operator: '<=', value: max });
};

const buildRegimePayload = (regime) => {
  const rpmMin = toPayloadNumber(regime.rpm_min);
  const rpmMax = toPayloadNumber(regime.rpm_max);
  const speedMin = toPayloadNumber(regime.speed_min_kmh);
  const speedMax = toPayloadNumber(regime.speed_max_kmh);
  const fuelMin = toPayloadNumber(regime.fuel_rate_min_lh);
  const fuelMax = toPayloadNumber(regime.fuel_rate_max_lh);
  const directions = (regime.directions || []).filter(Boolean);
  const geofenceNames = (regime.geofence_names || []).filter(Boolean);
  const conditions = [];

  addNumericConditions(conditions, 'avg_rpm', rpmMin, rpmMax);
  addNumericConditions(conditions, 'speed_kmh', speedMin, speedMax);
  addNumericConditions(conditions, 'fuel_rate_lh', fuelMin, fuelMax);
  if (directions.length) {
    conditions.push({ metric: 'direction', operator: 'in', values: directions });
  }
  geofenceNames.forEach((name) => {
    conditions.push({ metric: 'active_geofence', operator: 'contains', value: name });
  });

  return {
    id: regime.id || newRegimeId(),
    name: regime.name || 'Regime operacional',
    color: regime.color || '#0ea5e9',
    priority: toPayloadNumber(regime.priority) ?? 0,
    min_duration_minutes: toPayloadNumber(regime.min_duration_minutes) ?? 0,
    conditions,
    rpm_min: rpmMin,
    rpm_max: rpmMax,
    speed_min_kmh: speedMin,
    speed_max_kmh: speedMax,
    fuel_rate_min_lh: fuelMin,
    fuel_rate_max_lh: fuelMax,
    directions,
    geofence_names: geofenceNames,
  };
};

const buildRulePreview = (regime) => {
  const parts = [];
  if (regime.rpm_min || regime.rpm_max) parts.push(`RPM ${regime.rpm_min || '...'}-${regime.rpm_max || '...'}`);
  if (regime.speed_min_kmh || regime.speed_max_kmh) parts.push(`Vel. ${regime.speed_min_kmh || '...'}-${regime.speed_max_kmh || '...'} km/h`);
  if (regime.fuel_rate_min_lh || regime.fuel_rate_max_lh) parts.push(`Cons. ${regime.fuel_rate_min_lh || '...'}-${regime.fuel_rate_max_lh || '...'} L/h`);
  if (regime.directions?.length) parts.push(`Dir. ${regime.directions.join(', ')}`);
  if (regime.geofence_names?.length) parts.push(`Zona ${regime.geofence_names.join(', ')}`);
  return parts.length ? parts.join(' + ') : 'Sem condições';
};

const createDefaultOperationalProfile = (equipmentId) => ({
  id: 'default',
  name: 'Perfil padrão de auditoria',
  equipment_id: equipmentId || null,
  description: 'Perfil inicial editável para dragas e embarcações de apoio.',
  is_default: true,
  regimes: [
    {
      id: 'maneuver_empty',
      name: 'Saída/manobra vazia',
      color: '#38bdf8',
      priority: 40,
      min_duration_minutes: 2,
      speed_min_kmh: 0.5,
      speed_max_kmh: 8,
      fuel_rate_max_lh: 120,
      directions: ['NORTE', 'NORDESTE', 'NOROESTE'],
    },
    {
      id: 'loading',
      name: 'Carregamento',
      color: '#f59e0b',
      priority: 70,
      min_duration_minutes: 20,
      speed_max_kmh: 2,
      rpm_min: 700,
      rpm_max: 1900,
      directions: ['NORTE', 'NORDESTE', 'NOROESTE'],
    },
    {
      id: 'loaded_convoy',
      name: 'Navegação carregada comboiada',
      color: '#22c55e',
      priority: 80,
      min_duration_minutes: 10,
      rpm_min: 1000,
      rpm_max: 1400,
      speed_min_kmh: 3,
      speed_max_kmh: 11,
      directions: ['SUL', 'SUDESTE', 'SUDOESTE'],
    },
    {
      id: 'loaded_single',
      name: 'Navegação carregada descomboiada',
      color: '#16a34a',
      priority: 75,
      min_duration_minutes: 5,
      rpm_min: 900,
      rpm_max: 1500,
      speed_min_kmh: 1,
      speed_max_kmh: 7,
      directions: ['SUL', 'SUDESTE', 'SUDOESTE'],
    },
    {
      id: 'unloading',
      name: 'Descarga',
      color: '#a855f7',
      priority: 70,
      min_duration_minutes: 20,
      speed_max_kmh: 2,
      fuel_rate_min_lh: 5,
    },
    {
      id: 'return_empty',
      name: 'Retorno vazio',
      color: '#06b6d4',
      priority: 65,
      min_duration_minutes: 10,
      rpm_min: 800,
      rpm_max: 1500,
      speed_min_kmh: 3,
      directions: ['NORTE', 'NORDESTE', 'NOROESTE'],
    },
    {
      id: 'idle',
      name: 'Ocioso/indefinido',
      color: '#64748b',
      priority: 5,
      min_duration_minutes: 0,
      speed_max_kmh: 1,
      fuel_rate_max_lh: 5,
    },
  ],
});

const isOperationsApiNotReady = (error) => error?.response?.status === 404;

const FuelAuditPage = () => {
  const { formatDateTime } = useTimezone();
  const [equipments, setEquipments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState('default');
  const [start, setStart] = useState(defaultStart());
  const [stop, setStop] = useState(defaultStop());
  const [resolutionMinutes, setResolutionMinutes] = useState('5');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [report, setReport] = useState(null);
  const [operationsApiAvailable, setOperationsApiAvailable] = useState(true);
  const [profileName, setProfileName] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [regimes, setRegimes] = useState([]);

  const selectedEquipment = equipments.find((item) => item.id === selectedEquipmentId);
  const selectedProfile = profiles.find((item) => item.id === selectedProfileId) || profiles[0];
  const reportSummary = report?.regime_summary || report?.phase_summary || [];

  const loadEquipments = useCallback(async () => {
    const response = await operationsApi.getEquipments();
    const data = response.data || [];
    setEquipments(data);
    if (!selectedEquipmentId && data.length > 0) {
      setSelectedEquipmentId(data[0].id);
    }
  }, [selectedEquipmentId]);

  const loadProfiles = useCallback(async (equipmentId) => {
    if (!equipmentId) return;
    let data = [];
    try {
      const response = await operationsApi.getOperationalProfiles(equipmentId);
      data = response.data?.profiles || [];
      setOperationsApiAvailable(true);
    } catch (error) {
      if (!isOperationsApiNotReady(error)) {
        throw error;
      }
      setOperationsApiAvailable(false);
      data = [createDefaultOperationalProfile(equipmentId)];
    }
    setProfiles(data);
    setSelectedProfileId((current) => (data.some((profile) => profile.id === current) ? current : 'default'));
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await loadEquipments();
      } catch (error) {
        console.error('Error loading operational equipments:', error);
        toast.error('Erro ao carregar equipamentos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadEquipments]);

  useEffect(() => {
    loadProfiles(selectedEquipmentId).catch((error) => {
      console.error('Error loading operational profiles:', error);
      toast.error('Erro ao carregar perfis operacionais');
    });
  }, [selectedEquipmentId, loadProfiles]);

  useEffect(() => {
    if (!selectedProfile) return;
    setProfileName(selectedProfile.is_default ? `${selectedProfile.name} - copia` : selectedProfile.name || '');
    setProfileDescription(selectedProfile.description || '');
    setRegimes((selectedProfile.regimes || selectedProfile.phases || []).map(normalizeRegimeForEditor));
  }, [selectedProfile]);

  const updateRegime = (regimeId, patch) => {
    setRegimes((current) => current.map((regime) => (regime.id === regimeId ? { ...regime, ...patch } : regime)));
  };

  const toggleDirection = (regimeId, direction, checked) => {
    setRegimes((current) => current.map((regime) => {
      if (regime.id !== regimeId) return regime;
      const currentDirections = regime.directions || [];
      return {
        ...regime,
        directions: checked
          ? Array.from(new Set([...currentDirections, direction]))
          : currentDirections.filter((item) => item !== direction),
      };
    }));
  };

  const handleAnalyze = async () => {
    if (!selectedEquipmentId) {
      toast.error('Selecione um equipamento');
      return;
    }
    if (!operationsApiAvailable) {
      return;
    }
    setAnalyzing(true);
    try {
      const response = await operationsApi.analyzeOperations({
        equipment_id: selectedEquipmentId,
        start: new Date(start).toISOString(),
        stop: new Date(stop).toISOString(),
        profile_id: selectedProfileId,
        resolution_minutes: Number(resolutionMinutes),
        tank_mode: 'comparison',
      });
      setReport(response.data);
      toast.success('Análise gerada');
    } catch (error) {
      console.error('Error running operational analysis:', error);
      if (isOperationsApiNotReady(error)) {
        setOperationsApiAvailable(false);
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao gerar análise');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedEquipmentId) {
      toast.error('Selecione um equipamento');
      return;
    }
    if (!operationsApiAvailable) {
      return;
    }

    const payloadRegimes = regimes.map(buildRegimePayload);
    if (!payloadRegimes.length || payloadRegimes.some((regime) => !regime.name || regime.conditions.length === 0)) {
      toast.error('Cada regime precisa de nome e ao menos uma condição');
      return;
    }

    setSavingProfile(true);
    try {
      const payload = {
        name: profileName || 'Perfil operacional',
        description: profileDescription,
        equipment_id: selectedEquipmentId,
        regimes: payloadRegimes,
      };

      if (selectedProfileId && selectedProfileId !== 'default') {
        await operationsApi.updateOperationalProfile(selectedProfileId, payload);
      } else {
        const response = await operationsApi.createOperationalProfile(payload);
        setSelectedProfileId(response.data.id);
      }
      await loadProfiles(selectedEquipmentId);
      toast.success('Perfil salvo');
    } catch (error) {
      console.error('Error saving operational profile:', error);
      if (isOperationsApiNotReady(error)) {
        setOperationsApiAvailable(false);
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfileId || selectedProfileId === 'default' || !operationsApiAvailable) return;
    if (!window.confirm('Excluir este perfil operacional?')) return;
    setDeletingProfile(true);
    try {
      await operationsApi.deleteOperationalProfile(selectedProfileId);
      setSelectedProfileId('default');
      await loadProfiles(selectedEquipmentId);
      toast.success('Perfil excluído');
    } catch (error) {
      console.error('Error deleting operational profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir perfil');
    } finally {
      setDeletingProfile(false);
    }
  };

  const timelineTotal = useMemo(() => {
    return (report?.segments || []).reduce((total, segment) => total + Number(segment.duration_seconds || 0), 0) || 1;
  }, [report]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container space-y-6" data-testid="fuel-audit-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="pl-12 md:pl-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Fuel className="w-6 h-6 text-primary" />
            Análise de Combustível
          </h1>
          <p className="text-sm text-muted-foreground">
            Regimes, consumo, deslocamento e geofences
          </p>
        </div>
        <Button variant="outline" onClick={() => loadProfiles(selectedEquipmentId)} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      <Tabs defaultValue="analysis" className="space-y-4">
        <TabsList>
          <TabsTrigger value="analysis">Análise</TabsTrigger>
          <TabsTrigger value="profiles">Perfis Operacionais</TabsTrigger>
        </TabsList>

        {!operationsApiAvailable && (
          <Card className="border-yellow-500/40 bg-yellow-500/5">
            <CardContent className="py-3">
              <div className="flex items-start gap-2 text-sm text-yellow-300">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  API de Análise de Combustível aguardando publicação no backend. O perfil padrão pode ser visualizado, mas salvar perfis e gerar análises ficam bloqueados até o backend responder.
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Equipamento</Label>
                  <Select value={selectedEquipmentId} onValueChange={setSelectedEquipmentId}>
                    <SelectTrigger data-testid="fuel-audit-equipment">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipments.map((equipment) => (
                        <SelectItem key={equipment.id} value={equipment.id}>
                          {equipment.name} {equipment.tag ? `(${equipment.tag})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Perfil</Label>
                  <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                    <SelectTrigger data-testid="fuel-audit-profile">
                      <SelectValue placeholder="Perfil" />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.name}{profile.is_default ? ' (padrão)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Resolução</Label>
                  <Select value={resolutionMinutes} onValueChange={setResolutionMinutes}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 min</SelectItem>
                      <SelectItem value="5">5 min</SelectItem>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Início</Label>
                  <Input type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Fim</Label>
                  <Input type="datetime-local" value={stop} onChange={(event) => setStop(event.target.value)} />
                </div>
                <div className="md:col-span-3 flex items-end">
                  <Button onClick={handleAnalyze} disabled={analyzing || !operationsApiAvailable} className="gap-2">
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                    Gerar Análise
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {report && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard icon={Fuel} label="Combustível" value={`${numberFormat(report.summary?.total_fuel_liters)} L`} />
                <MetricCard icon={Timer} label="Duração" value={`${numberFormat(report.summary?.total_duration_hours, 2)} h`} />
                <MetricCard icon={Ship} label="Distância" value={`${numberFormat(report.summary?.total_distance_km)} km`} />
                <MetricCard icon={BarChart3} label="Média" value={`${numberFormat(report.summary?.avg_fuel_rate_lh)} L/h`} />
              </div>

              {report.quality_flags?.length > 0 && (
                <Card className="border-yellow-500/40 bg-yellow-500/5">
                  <CardContent className="py-4 space-y-2">
                    {report.quality_flags.map((flag, index) => (
                      <div key={`${flag}-${index}`} className="flex items-start gap-2 text-sm text-yellow-300">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{flag}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Linha do Tempo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex h-10 overflow-hidden rounded-md border">
                    {(report.segments || []).map((segment, index) => (
                      <div
                        key={`${segment.regime_id || segment.phase_id}-${index}`}
                        title={`${segment.regime_name || segment.phase_name} - ${numberFormat(segment.duration_hours, 2)}h`}
                        className="h-full"
                        style={{
                          width: `${Math.max(1, (Number(segment.duration_seconds || 0) / timelineTotal) * 100)}%`,
                          backgroundColor: segment.color,
                        }}
                      />
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {reportSummary.map((regime) => (
                      <Badge key={regime.regime_id || regime.phase_id} variant="outline" style={{ borderColor: regime.color, color: regime.color }}>
                        {regime.regime_name || regime.phase_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card className="xl:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Resumo por Regime</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Regime</TableHead>
                          <TableHead className="text-right">Tempo</TableHead>
                          <TableHead className="text-right">Litros</TableHead>
                          <TableHead className="text-right">L/h</TableHead>
                          <TableHead className="text-right">Distância</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportSummary.map((regime) => (
                          <TableRow key={regime.regime_id || regime.phase_id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: regime.color }} />
                                {regime.regime_name || regime.phase_name}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{numberFormat(regime.duration_hours, 2)} h</TableCell>
                            <TableCell className="text-right">{numberFormat(regime.fuel_liters)} L</TableCell>
                            <TableCell className="text-right">{numberFormat(regime.avg_fuel_rate_lh)} L/h</TableCell>
                            <TableCell className="text-right">{numberFormat(regime.distance_km)} km</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Motor x Tanque</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {report.tank_reconciliation ? (
                      <div className="space-y-3 text-sm">
                        <DetailRow label="Motores" value={`${numberFormat(report.tank_reconciliation.engine_total_liters)} L`} />
                        <DetailRow label="Tanques" value={`${numberFormat(report.tank_reconciliation.tank_consumed_liters)} L`} />
                        <DetailRow label="Diferença" value={`${numberFormat(report.tank_reconciliation.difference_liters)} L`} />
                        <DetailRow
                          label="Diferença %"
                          value={report.tank_reconciliation.difference_pct == null ? 'N/D' : `${numberFormat(report.tank_reconciliation.difference_pct)}%`}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">N/D</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Segmentos</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[360px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Regime</TableHead>
                          <TableHead>Início</TableHead>
                          <TableHead>Fim</TableHead>
                          <TableHead className="text-right">RPM</TableHead>
                          <TableHead className="text-right">Vel.</TableHead>
                          <TableHead className="text-right">Litros</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(report.segments || []).map((segment, index) => (
                          <TableRow key={`${segment.regime_id || segment.phase_id}-${segment.started_at}-${index}`}>
                            <TableCell>{segment.regime_name || segment.phase_name}</TableCell>
                            <TableCell>{formatDateTime(segment.started_at)}</TableCell>
                            <TableCell>{formatDateTime(segment.ended_at)}</TableCell>
                            <TableCell className="text-right">{numberFormat(segment.avg_rpm, 0)}</TableCell>
                            <TableCell className="text-right">{numberFormat(segment.avg_speed_kmh)} km/h</TableCell>
                            <TableCell className="text-right">{numberFormat(segment.fuel_liters)} L</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="profiles">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Perfil Operacional</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Equipamento</Label>
                  <Select value={selectedEquipmentId} onValueChange={setSelectedEquipmentId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Equipamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipments.map((equipment) => (
                        <SelectItem key={equipment.id} value={equipment.id}>{equipment.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Perfil</Label>
                  <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Perfil" />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>{profile.name}{profile.is_default ? ' (padrão)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input value={profileName} onChange={(event) => setProfileName(event.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input value={profileDescription} onChange={(event) => setProfileDescription(event.target.value)} />
              </div>

              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold">Regimes</h2>
                <Button
                  variant="outline"
                  onClick={() => setRegimes((current) => [...current, normalizeRegimeForEditor({ id: newRegimeId(), name: 'Novo regime' })])}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </Button>
              </div>

              <div className="space-y-4">
                {regimes.map((regime, index) => (
                  <div key={regime.id} className="rounded-md border p-4 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                      <div className="space-y-2 lg:col-span-1">
                        <Label>Cor</Label>
                        <Input
                          type="color"
                          value={regime.color}
                          onChange={(event) => updateRegime(regime.id, { color: event.target.value })}
                          className="h-10 p-1"
                        />
                      </div>
                      <div className="space-y-2 lg:col-span-5">
                        <Label>Nome</Label>
                        <Input value={regime.name} onChange={(event) => updateRegime(regime.id, { name: event.target.value })} />
                      </div>
                      <div className="space-y-2 lg:col-span-2">
                        <Label>Prioridade</Label>
                        <Input type="number" value={regime.priority} onChange={(event) => updateRegime(regime.id, { priority: event.target.value })} />
                      </div>
                      <div className="space-y-2 lg:col-span-2">
                        <Label>Duração min.</Label>
                        <Input type="number" value={regime.min_duration_minutes} onChange={(event) => updateRegime(regime.id, { min_duration_minutes: event.target.value })} />
                      </div>
                      <div className="flex items-end justify-end lg:col-span-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setRegimes((current) => current.filter((item) => item.id !== regime.id))}
                          disabled={regimes.length <= 1}
                          aria-label={`Remover regime ${index + 1}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <MetricRange
                        label="RPM"
                        minValue={regime.rpm_min}
                        maxValue={regime.rpm_max}
                        onMinChange={(value) => updateRegime(regime.id, { rpm_min: value })}
                        onMaxChange={(value) => updateRegime(regime.id, { rpm_max: value })}
                      />
                      <MetricRange
                        label="Velocidade km/h"
                        minValue={regime.speed_min_kmh}
                        maxValue={regime.speed_max_kmh}
                        onMinChange={(value) => updateRegime(regime.id, { speed_min_kmh: value })}
                        onMaxChange={(value) => updateRegime(regime.id, { speed_max_kmh: value })}
                      />
                      <MetricRange
                        label="Consumo L/h"
                        minValue={regime.fuel_rate_min_lh}
                        maxValue={regime.fuel_rate_max_lh}
                        onMinChange={(value) => updateRegime(regime.id, { fuel_rate_min_lh: value })}
                        onMaxChange={(value) => updateRegime(regime.id, { fuel_rate_max_lh: value })}
                      />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                      <div className="xl:col-span-2 space-y-2">
                        <Label>Direção</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {DIRECTION_OPTIONS.map((direction) => (
                            <label key={direction} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={(regime.directions || []).includes(direction)}
                                onCheckedChange={(checked) => toggleDirection(regime.id, direction, Boolean(checked))}
                              />
                              <span>{direction}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Geofences</Label>
                        <Input
                          value={(regime.geofence_names || []).join(', ')}
                          onChange={(event) => updateRegime(regime.id, {
                            geofence_names: event.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                          })}
                        />
                      </div>
                    </div>

                    <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      {buildRulePreview(regime)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {selectedEquipment?.name || 'Equipamento'} · {selectedProfile?.is_default ? 'Salvar cria uma cópia editável' : 'Perfil editável'}
                </p>
                <div className="flex items-center gap-2">
                  {selectedProfileId !== 'default' && (
                    <Button variant="outline" onClick={handleDeleteProfile} disabled={deletingProfile || !operationsApiAvailable} className="gap-2">
                      {deletingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Excluir
                    </Button>
                  )}
                  <Button onClick={handleSaveProfile} disabled={savingProfile || !operationsApiAvailable} className="gap-2">
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Salvar Perfil
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const MetricRange = ({ label, minValue, maxValue, onMinChange, onMaxChange }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <Input type="number" placeholder="Mín." value={minValue} onChange={(event) => onMinChange(event.target.value)} />
      <Input type="number" placeholder="Máx." value={maxValue} onChange={(event) => onMaxChange(event.target.value)} />
    </div>
  </div>
);

const MetricCard = ({ icon: Icon, label, value }) => (
  <Card>
    <CardContent className="py-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
        <Icon className="w-5 h-5 text-primary" />
      </div>
    </CardContent>
  </Card>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

export default FuelAuditPage;
